import type { EnCatalogue } from './locales/en';
import { catalogues } from './locales/index';
import { resolveMessage } from './resolve';
import {
  DEFAULT_LOCALE,
  LOCALES,
  type Locale,
  type MessageKey,
  type MessageParams,
  type ParameterizedKey,
  type ParameterlessKey,
} from './types';

/**
 * `t(locale, key, params)` - the only way a user-facing string reaches a surface.
 * No copy is hardcoded anywhere else (PRD NFR-006).
 *
 * Resolution order: the requested locale, then its fallback chain, then English.
 * A key that is missing everywhere is a programming error, not a runtime condition
 * - the `Catalogue` type and the completeness test both prevent it - so it
 * degrades to the key itself rather than showing a blank screen during a morning
 * service. The resolution itself lives in `resolve.ts` and is shared with
 * app-local catalogues built by `createCatalog()`.
 */

export function t<K extends ParameterlessKey>(locale: Locale, key: K): string;
export function t<K extends ParameterizedKey>(
  locale: Locale,
  key: K,
  params: MessageParams<K>,
): string;
export function t(
  locale: Locale,
  key: MessageKey,
  params?: Readonly<Record<string, string | number>>,
): string {
  return resolveMessage<EnCatalogue>(catalogues, locale, key, params);
}

/** True when the runtime locale string is one this build ships. */
export function isSupportedLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value);
}

/**
 * The best supported locale in an `Accept-Language` header or a browser language
 * list, or `null` when the reader asked for nothing this build ships.
 *
 * Separate from `resolveLocale` because "matched English" and "matched nothing"
 * are the same answer once the platform default has been applied, and one caller
 * has to tell them apart: a surface whose own default is not English. The
 * counter tablet is French (PRD BAK-025), so `@croissants/ui`'s locale provider
 * asks this and supplies its own fallback. Everything else wants
 * `resolveLocale`.
 */
export function matchLocale(
  preferred: readonly string[] | string | null | undefined,
): Locale | null {
  const candidates =
    typeof preferred === 'string'
      ? preferred.split(',').map((part) => (part.split(';')[0] ?? '').trim())
      : (preferred ?? []);

  for (const candidate of candidates) {
    if (!candidate) continue;
    const normalized = candidate.toLowerCase();
    if (isSupportedLocale(normalized)) return normalized;
    const base = normalized.split('-')[0];
    if (base && isSupportedLocale(base)) return base;
  }
  return null;
}

/**
 * Picks the best supported locale from an `Accept-Language` header or a browser
 * language list. Falls back to English, the platform default (PRD GST-006).
 */
export function resolveLocale(preferred: readonly string[] | string | null | undefined): Locale {
  return matchLocale(preferred) ?? DEFAULT_LOCALE;
}

/** Bound translator for a surface that already knows its locale. */
export function translatorFor(locale: Locale): {
  <K extends ParameterlessKey>(key: K): string;
  <K extends ParameterizedKey>(key: K, params: MessageParams<K>): string;
} {
  // biome-ignore lint/suspicious/noExplicitAny: the overloads above restore the types at the call site.
  const call = t as (...args: any[]) => string;
  return ((key: MessageKey, params?: Readonly<Record<string, string | number>>) =>
    call(locale, key, params)) as never;
}
